import { useCallback, useEffect, useRef } from 'react';

/**
 * useDrawerSwipe
 *
 * Hand-rolled touch gestures for the mobile navigation drawer (no gesture
 * library is installed). Provides two behaviours:
 *
 *  - Open: a left-edge → right swipe anywhere on the page opens the drawer.
 *    Listeners are attached on `window` as passive (never call preventDefault),
 *    so page scroll and pull-to-refresh are never blocked. The start point is
 *    only captured within EDGE_SIZE px of the left edge, which keeps the gesture
 *    from fighting vertical scroll / map panning in normal content.
 *  - Close: a right → left swipe on the drawer panel closes it. Returned as
 *    `panelHandlers` to spread onto the panel element.
 *
 * A swipe qualifies only when the horizontal delta exceeds MIN_DISTANCE and
 * dominates the vertical delta (so a diagonal scroll doesn't trigger it).
 */

/** Max distance (px) from the left edge where an opening swipe may start. */
const EDGE_SIZE = 24;
/** Min horizontal travel (px) for a swipe to count. */
const MIN_DISTANCE = 60;

interface Point {
  x: number;
  y: number;
}

interface UseDrawerSwipeOptions {
  /** When false, all gesture handling is disabled (e.g. on desktop). */
  enabled: boolean;
  /** Current open state of the drawer. */
  isOpen: boolean;
  /** Called when a left-edge → right swipe is detected. */
  onOpen: () => void;
  /** Called when a right → left swipe on the panel is detected. */
  onClose: () => void;
}

interface PanelHandlers {
  onTouchStart: (event: React.TouchEvent) => void;
  onTouchMove: (event: React.TouchEvent) => void;
  onTouchEnd: () => void;
}

export default function useDrawerSwipe({
  enabled,
  isOpen,
  onOpen,
  onClose,
}: UseDrawerSwipeOptions): { panelHandlers: PanelHandlers } {
  // ---- Open: left-edge swipe on window --------------------------------------
  useEffect(() => {
    if (!enabled || isOpen) {
      return;
    }

    let start: Point | null = null;

    const handleTouchStart = (event: TouchEvent) => {
      const touch = event.touches[0];
      start = touch.clientX <= EDGE_SIZE ? { x: touch.clientX, y: touch.clientY } : null;
    };

    const handleTouchMove = (event: TouchEvent) => {
      if (!start) {
        return;
      }
      const touch = event.touches[0];
      const dx = touch.clientX - start.x;
      const dy = touch.clientY - start.y;
      if (dx > MIN_DISTANCE && Math.abs(dx) > Math.abs(dy)) {
        start = null;
        onOpen();
      }
    };

    const handleTouchEnd = () => {
      start = null;
    };

    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchmove', handleTouchMove, { passive: true });
    window.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [enabled, isOpen, onOpen]);

  // ---- Close: swipe left on the panel ---------------------------------------
  const panelStart = useRef<Point | null>(null);

  const onTouchStart = useCallback((event: React.TouchEvent) => {
    const touch = event.touches[0];
    panelStart.current = { x: touch.clientX, y: touch.clientY };
  }, []);

  const onTouchMove = useCallback(
    (event: React.TouchEvent) => {
      if (!enabled || !panelStart.current) {
        return;
      }
      const touch = event.touches[0];
      const dx = touch.clientX - panelStart.current.x;
      const dy = touch.clientY - panelStart.current.y;
      if (dx < -MIN_DISTANCE && Math.abs(dx) > Math.abs(dy)) {
        panelStart.current = null;
        onClose();
      }
    },
    [enabled, onClose]
  );

  const onTouchEnd = useCallback(() => {
    panelStart.current = null;
  }, []);

  return { panelHandlers: { onTouchStart, onTouchMove, onTouchEnd } };
}
